import { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { EmptyAction } from 'typesafe-actions';

import { createVerificationCodeLinkAction, MobileVerificationState } from '../ducks/verificationCode';
import Nelo from './Nelo';

interface StateProps {
  phoneNumber: string | null;
}

interface DispatchProps {
  submitCreateVerificationCodeLink: () => void;
}

const MobileHandOff = (props: StateProps & DispatchProps): ReactElement => {
  const INITIAL_TIME = 120;

  const [timeLeft, setTimeLeft] = useState<number>(INITIAL_TIME);
  const { t } = useTranslation();
  const [disabled, setDisabled] = useState<boolean>(true);
  const [linksSent, setLinksSent] = useState<number>(1);

  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setDisabled(false);
    }
  }, [timeLeft]);

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;
  const timeDisplay = `${minutes}:${seconds.toString().padStart(2, '0')}`;

  return (
    <Nelo.BrandWrapper
      title={t('signup.mobileHandoff.title')}
      subtitle={t('signup.mobileHandoff.subtitle', { phone: props.phoneNumber })}
      progress={2}
      hideBackButton
    >
      <Nelo.GrayContainer bodyText={`${t('signup.mobileHandoff.body')} ${timeDisplay}`} />
      <div
        className={`
          text-left mt-4 transition-opacity
          ${disabled ? 'text-gray-400 cursor-not-allowed' : 'text-primary cursor-pointer'}
        `}
        onClick={(): void => {
          if (!disabled && linksSent < 3) {
            setDisabled(true);
            setTimeLeft(INITIAL_TIME);
            setLinksSent(prev => prev + 1);
            props.submitCreateVerificationCodeLink();
          }
        }}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
      >
        {t('signup.mobileHandoff.cta')}
      </div>
    </Nelo.BrandWrapper>
  );
};

const mapStateToProps = ({ verificationCode }: { verificationCode: MobileVerificationState }): StateProps => ({
  phoneNumber: verificationCode.phoneNumberE164
});

const mapDispatchToProps = {
  submitCreateVerificationCodeLink: (): EmptyAction<'mobileVerification/CREATE_VERIFICATION_CODE_LINK'> =>
    createVerificationCodeLinkAction()
};

export default connect(mapStateToProps, mapDispatchToProps)(MobileHandOff);
